// Action buttons for paused resolution tier — accept, send to user, reject, skip
import { Button } from '@/components/ui/button'
import { CheckCircle, ArrowRight, Users, FileDown, Send } from 'lucide-react'
import type { ResolutionTierName, GeneratedInstructions } from '@/types/issue'

interface TierActionButtonsProps {
  tier: ResolutionTierName
  confidence: number
  instructions: GeneratedInstructions | null
  onAccept: () => void
  onReject: () => void
  onSkipToExpert: () => void
  onSendToUser: () => void
  onExportPDF: () => void
  onExportDOCX: () => void
}

const NEXT_TIER_LABEL: Record<string, string> = {
  kb: 'Search Microsoft Docs',
  ms_docs: 'Search D365 Community',
  community: 'Assign to Expert',
}

export function TierActionButtons({
  tier, confidence, instructions,
  onAccept, onReject, onSkipToExpert, onSendToUser, onExportPDF, onExportDOCX,
}: TierActionButtonsProps): JSX.Element {
  const confidenceText = confidence > 0
    ? `${(confidence * 100).toFixed(0)}% confidence`
    : 'No confidence score'

  const isExpertTier = tier === 'expert'
  const hasInstructions = instructions && instructions.steps.length > 0

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-kaycoo-teal-light bg-kaycoo-teal-pale/30 p-3">
      <p className="text-xs font-medium text-kaycoo-charcoal-2">
        {isExpertTier
          ? 'Expert has been assigned to this issue.'
          : `Results found (${confidenceText}). What would you like to do?`}
      </p>

      <div className="flex flex-wrap gap-2">
        {!isExpertTier && hasInstructions && (
          <Button size="sm" onClick={onSendToUser}
            className="gap-1.5 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
            <Send className="h-3.5 w-3.5" />
            Send to User & Wait Feedback
          </Button>
        )}

        <Button size="sm" onClick={onAccept}
          className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700">
          <CheckCircle className="h-3.5 w-3.5" />
          {isExpertTier ? 'Confirm Assignment' : 'Mark Resolved'}
        </Button>

        {!isExpertTier && (
          <Button size="sm" variant="outline" onClick={onReject}
            className="gap-1.5 border-kaycoo-charcoal-2 text-kaycoo-charcoal">
            <ArrowRight className="h-3.5 w-3.5" />
            {NEXT_TIER_LABEL[tier] || 'Continue Search'}
          </Button>
        )}

        {!isExpertTier && tier !== 'community' && (
          <Button size="sm" variant="ghost" onClick={onSkipToExpert}
            className="gap-1.5 text-purple-700 hover:bg-purple-50">
            <Users className="h-3.5 w-3.5" />
            Assign to Expert
          </Button>
        )}
      </div>

      {hasInstructions && !isExpertTier && (
        <div className="flex items-center gap-2 border-t border-kaycoo-white-3 pt-2">
          <span className="text-[10px] uppercase tracking-wide text-kaycoo-charcoal-2">Export:</span>
          <Button size="sm" variant="ghost" onClick={onExportPDF}
            className="h-6 gap-1 px-2 text-xs text-kaycoo-charcoal-2 hover:text-kaycoo-teal-dark">
            <FileDown className="h-3 w-3" /> PDF
          </Button>
          <Button size="sm" variant="ghost" onClick={onExportDOCX}
            className="h-6 gap-1 px-2 text-xs text-kaycoo-charcoal-2 hover:text-kaycoo-teal-dark">
            <FileDown className="h-3 w-3" /> DOCX
          </Button>
        </div>
      )}
    </div>
  )
}
